import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { accountsApi, transfersApi, type Account, type Transfer } from '@/api';
import { formatBRL } from '@/lib/utils';
import { ApiError } from '@/lib/api';

type SortColumn = 'date' | 'fromAccount' | 'toAccount' | 'amount' | 'description';

export function TransferenciasPage() {
  const qc = useQueryClient();

  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState<Transfer | null>(null);
  const [removing, setRemoving] = useState<Transfer | null>(null);

  const [sort, setSort] = useState<SortState<SortColumn>>({ column: 'date', dir: 'desc' });
  const toggleSort = makeToggleSort(setSort);

  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: accountsApi.list });
  const { data, isLoading } = useQuery({
    queryKey: ['transfers'],
    queryFn: () => transfersApi.list({ take: 200 }),
  });

  const sortedItems = useMemo(() => {
    const rows = data?.items ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'date':        primary = cmp.date(a.date, b.date); break;
        case 'fromAccount': primary = cmp.text(a.fromAccount.name, b.fromAccount.name); break;
        case 'toAccount':   primary = cmp.text(a.toAccount.name, b.toAccount.name); break;
        case 'amount':      primary = cmp.number(a.amount, b.amount); break;
        case 'description': primary = cmp.text(a.description ?? '', b.description ?? ''); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.date(b.date, a.date);
    });
  }, [data, sort]);

  function refresh() {
    qc.invalidateQueries({ queryKey: ['transfers'] });
    qc.invalidateQueries({ queryKey: ['transactions'] });
    qc.invalidateQueries({ queryKey: ['transactions-summary'] });
    qc.invalidateQueries({ queryKey: ['accounts'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  }

  return (
    <>
      <PageHeader
        title="Transferências entre caixas"
        breadcrumb="Cadastros"
        actions={
          <Button size="sm" onClick={() => setOpenNew(true)}>
            <Plus className="w-4 h-4" /> Nova transferência
          </Button>
        }
      />
      <PageBody>
        <div className="bg-card border rounded-lg p-3 text-xs text-muted-foreground flex items-start gap-2">
          <ArrowRightLeft className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            Transferências movimentam valores de um caixa para outro. Aparecem no Caixa
            como um par entrada/saída marcado com badge <strong>Transferência</strong>,
            mas <strong>não entram nos somatórios</strong> de Recebido/Pago — servem
            apenas como ajuste interno. Editar ou remover aqui reflete imediatamente
            nos saldos das contas envolvidas.
          </p>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="date"        label="Data"      sort={sort} onClick={toggleSort} />
                <SortHeader column="fromAccount" label="De"        sort={sort} onClick={toggleSort} />
                <SortHeader column="toAccount"   label="Para"      sort={sort} onClick={toggleSort} />
                <SortHeader column="amount"      label="Valor"     sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="description" label="Descrição" sort={sort} onClick={toggleSort} />
                <th className="px-3 py-2 font-medium w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    Carregando…
                  </td>
                </tr>
              )}
              {sortedItems.map((t) => (
                <tr key={t.id} className="group">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {new Date(t.date).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-3 py-2">{t.fromAccount.name}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <ArrowRightLeft className="w-3 h-3" />
                      <span className="text-foreground">{t.toAccount.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-medium">
                    {formatBRL(t.amount)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {t.description || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditing(t)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        title="Editar"
                        aria-label="Editar transferência"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setRemoving(t)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                        title="Remover"
                        aria-label="Remover transferência"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (data?.items.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    Nenhuma transferência registrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      {openNew && (
        <TransferFormDialog
          editing={null}
          accounts={accounts ?? []}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); refresh(); }}
        />
      )}
      {editing && (
        <TransferFormDialog
          editing={editing}
          accounts={accounts ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {removing && (
        <RemoveTransferDialog
          target={removing}
          onClose={() => setRemoving(null)}
          onDone={() => { setRemoving(null); refresh(); }}
        />
      )}
    </>
  );
}

// =====================  Form (criar / editar)  =====================

function TransferFormDialog({
  editing,
  accounts,
  onClose,
  onSaved,
}: {
  editing: Transfer | null;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [fromAccountId, setFromAccountId] = useState(editing?.fromAccount.id ?? '');
  const [toAccountId, setToAccountId] = useState(editing?.toAccount.id ?? '');
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '');
  const [date, setDate] = useState(
    editing ? editing.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [description, setDescription] = useState(editing?.description ?? '');

  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.active || a.id === fromAccountId || a.id === toAccountId),
    [accounts, fromAccountId, toAccountId],
  );

  const sameAccount = !!fromAccountId && fromAccountId === toAccountId;

  const create = useMutation({
    mutationFn: () =>
      transfersApi.create({
        fromAccountId,
        toAccountId,
        amount: Number(amount),
        date,
        description: description.trim() || undefined,
      }),
    onSuccess: onSaved,
  });

  const update = useMutation({
    mutationFn: () =>
      transfersApi.update(editing!.id, {
        fromAccountId,
        toAccountId,
        amount: Number(amount),
        date,
        description: description.trim() || '',
      }),
    onSuccess: onSaved,
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!fromAccountId || !toAccountId || sameAccount) return;
    if (!amount || Number(amount) <= 0) return;
    mut.mutate();
  }

  return (
    <Dialog open onClose={onClose} title={isEdit ? 'Editar transferência' : 'Nova transferência'}>
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>De (origem)</Label>
              <Select value={fromAccountId} onChange={(e) => setFromAccountId(e.target.value)}>
                <option value="">Selecione…</option>
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Para (destino)</Label>
              <Select value={toAccountId} onChange={(e) => setToAccountId(e.target.value)}>
                <option value="">Selecione…</option>
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
          </div>
          {sameAccount && (
            <p className="text-xs text-destructive">
              Conta de origem e destino devem ser diferentes.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Data</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Descrição (opcional)</Label>
            <Input
              maxLength={500}
              placeholder="Ex.: Acerto entre caixas, depósito em conta…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            disabled={
              !fromAccountId || !toAccountId || sameAccount ||
              !amount || Number(amount) <= 0 || mut.isPending
            }
          >
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Registrar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// =====================  Remoção  =====================

function RemoveTransferDialog({
  target,
  onClose,
  onDone,
}: {
  target: Transfer;
  onClose: () => void;
  onDone: () => void;
}) {
  const mut = useMutation({
    mutationFn: () => transfersApi.remove(target.id),
    onSuccess: onDone,
  });
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  return (
    <Dialog open onClose={onClose} title="Remover transferência">
      <DialogBody className="space-y-3">
        <p>
          Remover a transferência de{' '}
          <strong>{formatBRL(target.amount)}</strong> de{' '}
          <strong>{target.fromAccount.name}</strong> para{' '}
          <strong>{target.toAccount.name}</strong>?
        </p>
        <p className="text-sm text-muted-foreground">
          Os 2 lançamentos correspondentes serão excluídos do Caixa e os saldos das
          contas envolvidas voltarão ao estado anterior à transferência.
        </p>
        {apiError && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
            {apiError}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant="destructive"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Removendo…' : 'Remover'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
